'use client';

import * as React from 'react';
import { Avatar as V2Avatar } from '@ghq-abi/design-system-v2';

import { useGetProfilePicture } from '~/shared/hooks';
import { cn } from '~/shared/utils/cn';
import { invertColor, stringToColor } from '~/shared/utils/color';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type AvatarProps = {
  name: string;
  src?: string;
  size?: AvatarSize;
  className?: string;
  imageClassName?: string;
  employeeId?: string;
  globalId?: string;
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
};

const sizeToClasses: Record<AvatarSize, string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-12 w-12 text-sm',
  lg: 'h-16 w-16 text-base',
  xl: 'h-20 w-20 text-lg',
};

function getInitials(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const Avatar = React.forwardRef<HTMLImageElement, AvatarProps>(
  (
    { name, src, size = 'sm', className, imageClassName, employeeId, globalId, onLoad },
    forwardedRef
  ) => {
    const { imageSrc } = useGetProfilePicture(globalId ?? '');
    const resolvedSrc =
      (employeeId || globalId) && imageSrc ? imageSrc : src;

    const background = React.useMemo(() => stringToColor(name), [name]);
    const color = React.useMemo(() => `#${invertColor(background, true)}`, [
      background,
    ]);

    return (
      <V2Avatar.Root
        className={cn(
          'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full font-medium',
          sizeToClasses[size],
          className
        )}
        title={name}
      >
        {resolvedSrc && (
          <V2Avatar.Image
            ref={forwardedRef}
            src={resolvedSrc}
            alt={name}
            onLoad={onLoad}
            className={cn('h-full w-full object-cover', imageClassName)}
          />
        )}
        <V2Avatar.Fallback
          className="flex h-full w-full items-center justify-center"
          style={{ backgroundColor: background, color }}
        >
          {getInitials(name)}
        </V2Avatar.Fallback>
      </V2Avatar.Root>
    );
  }
);

Avatar.displayName = 'Avatar';
